import {
  FeatureName,
  useCurrentAccount,
  useCurrentWorkspace,
  useFeatures,
  useIsFeatureEnabled,
  WorkspaceType,
} from '@gripp/shared-logic';
// Crop Circle tokens (`colors`), not the mutable `Colors`: this route can
// render before setUseCropCircleDesign() runs, when Colors.primary still
// holds the legacy orange and would mismatch the native launch screen.
import { colors, LoginHeader } from '@gripp/shared-ui';
import { useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { ActivityIndicator, PaperProvider } from 'react-native-paper';
import { useNavigate } from 'react-router-dom';

// Render nothing for this long so fast loads never flash the splash — the
// page stays browser-white until the landing decision resolves. Only loads
// slower than this show the splash below.
const SPLASH_APPEARANCE_DELAY_MS = 500;

export const Home = () => {
  const navigate = useNavigate();

  // Landing decision (OEM Home Dashboard, PR 1b): flag-on OEM lands on /home;
  // every other workspace type and every flag-off case lands on /activities.
  // To avoid a flash/bounce, keep showing the splash below until the account
  // and features have settled, then decide exactly once.
  const workspace = useCurrentWorkspace();
  const { account, isFetching: accountFetching } = useCurrentAccount();
  const { data: features, isLoading: featuresLoading } = useFeatures();
  const isHomeDashboardEnabled = useIsFeatureEnabled(
    FeatureName.OemHomeDashboard
  );
  // The dashboard is built entirely in Crop Circle (glass surfaces, tokenized
  // chrome) and assumes the CC skin — without it the page renders under the
  // legacy Header and reads as broken. Gate landing on CC too, so the landing
  // decision, the page self-guard, and the nav reorder all agree on the same
  // three conditions (CC + OEM Home flag + OEM workspace).
  const isCropCircleEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );

  const [splashVisible, setSplashVisible] = useState(false);
  useEffect(() => {
    const timer = setTimeout(
      () => setSplashVisible(true),
      SPLASH_APPEARANCE_DELAY_MS
    );
    return () => clearTimeout(timer);
  }, []);

  // Wait only while the account or features are GENUINELY still loading — not
  // on a workspace that is legitimately absent. useCurrentWorkspace() returns
  // null/undefined both while fetching AND for accounts with no resolved
  // workspace (a global admin who hasn't selected/impersonated one, lonely
  // larry). Gating on `workspace` being truthy stranded those users on this
  // splash forever; instead settle on the account, then treat a falsy
  // workspace as "not OEM" and fall through to /activities.
  const accountResolving = !account && accountFetching;
  const featuresResolving = featuresLoading || !features;
  const isResolving = accountResolving || featuresResolving;

  useEffect(() => {
    if (isResolving) return;

    const isOem = workspace?.type === WorkspaceType.Oem;
    const landOnHome = isOem && isHomeDashboardEnabled && isCropCircleEnabled;
    navigate(landOnHome ? '/home' : '/activities', {
      replace: true,
    });
  }, [
    isResolving,
    workspace,
    isHomeDashboardEnabled,
    isCropCircleEnabled,
    navigate,
  ]);

  if (!splashVisible) {
    return null;
  }

  // White theme matching the native launch screen and SplashMessageScreen,
  // so the splash blends into the white app content instead of flashing.
  return (
    <PaperProvider>
      <View style={styles.container}>
        <View style={styles.headerImage}>
          <LoginHeader fillColor={colors.brand.core} />
        </View>
        <ActivityIndicator
          size="large"
          animating={true}
          color={colors.brand.core}
        />
      </View>
    </PaperProvider>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors.brand.white,
    flex: 1,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerImage: {
    paddingBottom: 65,
  },
});
